/**
 * Connection-specific context menu actions.
 * Handles actions at the connection (server) level.
 */

import { ContextMenuAction } from '../../explorer/models/TreeModel';
import type { TreeNode } from '../../explorer/models/TreeModel';
import { toast } from '../../../components/ui/toaster';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { openDeadlockAnalyzerTab } from '../../workspace/deadlock-analyzer/openDeadlockAnalyzerTab';

/**
 * Connection-specific context menu action types
 */
type ConnectionContextMenuActionType =
  | 'refresh'
  | 'reconnect'
  | 'disconnect'
  | 'import-bacpac'
  | 'connection-info'
  | 'edit-connection'
  | 'session-browser'
  | 'tablespace-manager'
  | 'instance-monitor'
  | 'data-pump'
  | 'deadlock-analyzer';

const ConnectionActions = {
  REFRESH: 'refresh' as ConnectionContextMenuActionType,
  RECONNECT: 'reconnect' as ConnectionContextMenuActionType,
  DISCONNECT: 'disconnect' as ConnectionContextMenuActionType,
  IMPORT_BACPAC: 'import-bacpac' as ConnectionContextMenuActionType,
  CONNECTION_INFO: 'connection-info' as ConnectionContextMenuActionType,
  EDIT_CONNECTION: 'edit-connection' as ConnectionContextMenuActionType,
  SESSION_BROWSER: 'session-browser' as ConnectionContextMenuActionType,
  TABLESPACE_MANAGER: 'tablespace-manager' as ConnectionContextMenuActionType,
  INSTANCE_MONITOR: 'instance-monitor' as ConnectionContextMenuActionType,
  DATA_PUMP: 'data-pump' as ConnectionContextMenuActionType,
  DEADLOCK_ANALYZER: 'deadlock-analyzer' as ConnectionContextMenuActionType,
};

/**
 * Create a separator item
 */
function createSeparator(): ContextMenuAction {
  return {
    id: 'separator',
    label: '',
    isSeparator: true,
  };
}

/**
 * Create a refresh action
 */
function createRefreshAction(): ContextMenuAction {
  return {
    id: ConnectionActions.REFRESH,
    label: 'Refresh',
    icon: 'refresh',
  };
}

/**
 * Create a reconnect action — full rebuild of pools/auth state. The escape
 * hatch for "queries time out until I restart the app" wedge states.
 */
function createReconnectAction(): ContextMenuAction {
  return {
    id: ConnectionActions.RECONNECT,
    label: 'Reconnect',
    icon: 'plug-zap',
  };
}

/**
 * Create a disconnect action
 */
function createDisconnectAction(): ContextMenuAction {
  return {
    id: ConnectionActions.DISCONNECT,
    label: 'Disconnect',
    icon: 'power-off',
  };
}

/**
 * Create "Import Data-tier Application (.bacpac)..." action for connection node
 */
function createImportBacpacAction(): ContextMenuAction {
  return {
    id: ConnectionActions.IMPORT_BACPAC,
    label: 'Import Data-tier Application (.bacpac)...',
    icon: 'upload',
  };
}

/**
 * Create "Connection Info" action
 */
function createConnectionInfoAction(): ContextMenuAction {
  return {
    id: ConnectionActions.CONNECTION_INFO,
    label: 'Connection Info',
    icon: 'info',
  };
}

/**
 * Create "Edit Connection..." action
 */
function createEditConnectionAction(): ContextMenuAction {
  return {
    id: ConnectionActions.EDIT_CONNECTION,
    label: 'Edit Connection...',
    icon: 'edit',
  };
}

/**
 * Generate connection-specific context menu actions.
 * Only shows MSSQL-specific actions for MSSQL connections.
 */
export function generateConnectionContextMenuActions(engine?: string): ContextMenuAction[] {
  const actions: ContextMenuAction[] = [];

  // Copy name action
  actions.push({ id: 'copy-name', label: 'Copy connection name', icon: 'clipboard' });

  // Connection info + edit actions (always available)
  actions.push(createConnectionInfoAction());
  actions.push(createEditConnectionAction());
  actions.push(createSeparator());

  // Backup/restore actions (MSSQL only)
  if (engine === 'mssql' || engine === 'mock') {
    actions.push(createImportBacpacAction());
    // Import/inspect a deadlock report (.xdl/XML); live collection from this
    // connection is a later slice (F1-B) and reuses the same tab.
    actions.push({ id: ConnectionActions.DEADLOCK_ANALYZER, label: 'Deadlock Analyzer', icon: 'lock' });
    actions.push(createSeparator());
  }

  // Oracle admin tools
  if (engine === 'oracle') {
    actions.push(
      { id: ConnectionActions.SESSION_BROWSER, label: 'Session Browser', icon: 'users' },
      { id: ConnectionActions.TABLESPACE_MANAGER, label: 'Tablespace Manager', icon: 'hard-drive' },
      { id: ConnectionActions.INSTANCE_MONITOR, label: 'Instance Monitor', icon: 'activity' },
      { id: ConnectionActions.DATA_PUMP, label: 'Data Pump', icon: 'database-backup' },
    );
    actions.push(createSeparator());
  }

  // EVERY engine (K2, ruled 2026-09-05). `connect(config, { force })` is
  // honoured by all of them — the shared lifecycle helper treats `force` as
  // stale outright, with no smoke test; SQLite re-opens its handle and Kusto
  // rebuilds its client — so the old `mssql | mock` gate was a UI gap, not an
  // engine limitation, and it left the "queries time out until I restart the
  // app" escape hatch unreachable on the five engines most likely to need it.
  // See Docs/DbConnection.md → "Force Reconnect".
  actions.push(createReconnectAction());
  // Common actions
  actions.push(createDisconnectAction());
  actions.push(createRefreshAction());

  return actions;
}

/**
 * Open an Oracle admin tool tab from the connection context menu
 */
function openOracleAdminTab(
  connectionId: string,
  node: TreeNode,
  type: 'session-browser' | 'tablespace-manager' | 'instance-monitor' | 'data-pump',
  titlePrefix: string,
  content: Record<string, unknown>,
): void {
  const { addTab } = useWorkspaceStore.getState();
  const connectionName = node.name;
  const serverHost = (node.metadata?.url as string) || '';

  addTab({
    title: `${titlePrefix}: ${connectionName}`,
    type,
    connectionInfo: {
      connectionId,
      connectionName,
      serverHost,
    },
    content,
  });
}

/**
 * Execute a connection context menu action
 */
export function executeConnectionContextMenuAction(
  actionId: string,
  nodeId: string,
  handlers: {
    onRefresh?: (nodeId: string) => void;
    onReconnect?: (connectionId: string) => void;
    onDisconnect?: (connectionId: string) => void;
    onImportBacpac?: (connectionId: string) => void;
    onConnectionInfo?: (connectionId: string) => void;
    onEditConnection?: (connectionId: string) => void;
  } = {},
  node?: TreeNode
): void {
  // Handle refresh separately
  if (actionId === ConnectionActions.REFRESH) {
    if (handlers.onRefresh) {
      handlers.onRefresh(nodeId);
    }
    return;
  }

  const connectionId = node?.metadata?.id as string | undefined;

  switch (actionId) {
    case ConnectionActions.RECONNECT:
      if (connectionId && handlers.onReconnect) {
        handlers.onReconnect(connectionId);
      } else if (!handlers.onReconnect) {
        toast.error({ title: 'Error', description: 'Reconnect handler not available' });
      }
      break;

    case ConnectionActions.DISCONNECT:
      if (connectionId && handlers.onDisconnect) {
        handlers.onDisconnect(connectionId);
      } else if (!handlers.onDisconnect) {
        toast.error({ title: 'Error', description: 'Disconnect handler not available' });
      }
      break;

    case ConnectionActions.IMPORT_BACPAC:
      if (connectionId && handlers.onImportBacpac) {
        handlers.onImportBacpac(connectionId);
      } else if (!handlers.onImportBacpac) {
        toast.error({ title: 'Error', description: 'Import handler not available' });
      } else {
        toast.error({ title: 'Error', description: 'Connection not found' });
      }
      break;

    case ConnectionActions.CONNECTION_INFO:
      if (connectionId && handlers.onConnectionInfo) {
        handlers.onConnectionInfo(connectionId);
      } else if (!handlers.onConnectionInfo) {
        toast.error({ title: 'Error', description: 'Connection info handler not available' });
      } else {
        toast.error({ title: 'Error', description: 'Connection not found' });
      }
      break;

    case ConnectionActions.EDIT_CONNECTION:
      if (connectionId && handlers.onEditConnection) {
        handlers.onEditConnection(connectionId);
      } else if (!handlers.onEditConnection) {
        toast.error({ title: 'Error', description: 'Edit connection handler not available' });
      } else {
        toast.error({ title: 'Error', description: 'Connection not found' });
      }
      break;

    case ConnectionActions.SESSION_BROWSER:
      if (connectionId && node) {
        openOracleAdminTab(connectionId, node, 'session-browser', 'Sessions', {
          filter: 'active',
          autoRefreshInterval: 0,
          isLoading: true,
        });
      }
      break;

    case ConnectionActions.TABLESPACE_MANAGER:
      if (connectionId && node) {
        openOracleAdminTab(connectionId, node, 'tablespace-manager', 'Tablespaces', {
          isLoading: true,
        });
      }
      break;

    case ConnectionActions.INSTANCE_MONITOR:
      if (connectionId && node) {
        openOracleAdminTab(connectionId, node, 'instance-monitor', 'Instance', {
          activePanel: 'dashboard',
          autoRefreshInterval: 0,
          isLoading: true,
        });
      }
      break;

    case ConnectionActions.DATA_PUMP:
      if (connectionId && node) {
        openOracleAdminTab(connectionId, node, 'data-pump', 'Data Pump', {
          mode: 'export',
          wizardStep: 'prerequisites',
          isLoading: true,
        });
      }
      break;

    case ConnectionActions.DEADLOCK_ANALYZER:
      if (connectionId && node) {
        openDeadlockAnalyzerTab({
          entry: 'context-menu',
          connectionInfo: { connectionId, connectionName: node.name, serverHost: (node.metadata?.url as string) || '' },
        });
      }
      break;

    default:
      // Ignore unknown actions
      break;
  }
}
